/**
 * The trajectory panel: the walk, in order, and what differs along it (`V131`, `V132`, P27, P28).
 *
 * ## The panel is the record; a card on the map is a view of it
 *
 * The reader's decision, 2026-08-28, and it is the rule the whole module is built around. A
 * trajectory is an **ordering the reader constructed**, and the ordering is not visible on the map
 * at all — the cards carry step numbers, but nothing on the canvas says which card comes after
 * which. So this list is the artefact, and the gestures that clear the map leave it alone:
 *
 * | Gesture | The card | The trajectory |
 * |---|---|---|
 * | a card's own `×`, `clear N cards`, Escape | closed | **untouched** |
 * | a projection change | dropped (pinned) or re-filtered (decoded), as before | **untouched** — see `place` below |
 * | **this list's `×`** | **left on the map, as an ordinary card** | entry removed |
 * | this list's `clear the trajectory` | left on the map | emptied |
 * | right-click a decoded point, or `clear N decoded points` | deleted, record and all | **the entry is removed too** (`V133`) |
 * | `CAP_PER_VIEW` evicts the oldest of 200 decoded records | dropped on the next view change | **entry kept, and marked `gone`** |
 * | an atlas change | dropped | emptied — a row index names a different peptide |
 *
 * The third row is the reader's other correction: *"removing something doesn't mean closing its
 * card, just that it won't be part of the highlighting trajectory."* So removal takes the step
 * label and the highlight off that card and leaves the card, which is why `main.ts` rebuilds
 * **every** card after any change here rather than only the members'.
 *
 * The fifth and sixth rows are the one case where the thing an entry names can cease to exist, and
 * they are split because **only one of them is a gesture** (`V133`). A deletion is deliberate and
 * about that point, so the entry goes with it — the steps below renumber, which is the accepted cost.
 * An eviction is not a gesture at all: the reader decoded a 201st point somewhere else on the map, so
 * the entry stays and says `gone` rather than renumbering a walk nobody touched.
 *
 * ## `place`, and the three reasons an entry may not be placeable
 *
 * `EntryStatus` in `trajectory.ts` is the whole of it, computed by `main.ts` because only `main.ts`
 * knows what is pinned, what is drawn and which projection is on screen. This module turns one
 * value into one control and one phrase, and decides nothing.
 *
 * The `off-projection` case is `V115`: an entry holds *its own projection's* coordinates, so a
 * projection change keeps the entry — the sequence is still true — and invalidates those two
 * numbers. Rather than place a card somewhere wrong (eight landmarks 11,000 px off a 1,200 px
 * canvas), such an entry says so and its `place` control is disabled.
 *
 * ## All three kinds of point, in their own colours (P9.5, `V137`)
 *
 * A stored entry's sequence is `sequence stored`, a generated one's `sequence generated`, a typed
 * one's `sequence typed`, and the facts line above names which it is. **The list is the one
 * surface where all three sit adjacent**, so it is the one where blurring them would be easiest
 * and worst — and the reason `styleOf` lives in `trajectory.ts` rather than as a ternary here.
 *
 * ## Reorder by buttons
 *
 * Up and down, not drag-and-drop. Keyboard-reachable, no new machinery — the card drag is ~150
 * lines with a threshold, a resize-corner carve-out and a zoom-scaled clamp (`V128`, `V130`) — and
 * assertable without synthesising pointer sequences.
 */
import {
  diffLabel,
  keyOf,
  originOf,
  sequenceOf,
  styleOf,
  type Baseline,
  type EntryStatus,
  type Step,
  type TrajectoryPoint,
} from '../trajectory';
import { residueSpans } from './residues';

/**
 * What `place` promises, per provenance.
 *
 * A table rather than a ternary because there are three of them now, and because each one has to
 * say what the gesture actually does: a stored point's card is *put back*, a decoded or encoded
 * point's card is *reopened* — the record was never gone, only its card.
 */
const PLACE_TITLE: Record<TrajectoryPoint['kind'], string> = {
  stored: 'put this point’s card back on the map',
  generated: 'reopen this decoded point’s card on the map',
  encoded: 'reopen this typed sequence’s card on the map',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) node.append(child);
  return node;
}

/** Everything the panel does, as the pane's own callbacks. Indices are positions in `points`. */
export interface TrajectoryHandlers {
  onMove(at: number, by: -1 | 1): void;
  /** Take the entry out of the walk. **Never closes its card** — see the module docstring. */
  onRemove(at: number): void;
  /** Open that point in the detail pane, without needing to find it on the map again. */
  onSelect(at: number): void;
  /** Put that point's card back on the map. Only offered for a `placeable` entry. */
  onPlace(at: number): void;
  onPlaceAll(): void;
  onClear(): void;
  onBaseline(baseline: Baseline): void;
}

/** What the panel draws. Everything derived is derived by the caller, so this stays a renderer. */
export interface TrajectoryView {
  readonly points: readonly TrajectoryPoint[];
  /** Aligned with `points`; from `steps(points, baseline)`. */
  readonly steps: readonly Step[];
  readonly baseline: Baseline;
  /**
   * Per entry, keyed by `keyOf`: whether its card is up and whether it can be put back (`V132`).
   *
   * A map rather than a `placed` set, because "not on the map" has three causes and each needs its
   * own sentence. A key with no entry is treated as `placeable`, which is the harmless default: the
   * control is offered and `main.ts`'s `placePoint` refuses anything it should not do.
   */
  readonly status: ReadonlyMap<string, EntryStatus>;
  /** Whether this pane is in trajectory mode, which decides the empty state. */
  readonly active: boolean;
}

export interface TrajectoryPanel {
  show(view: TrajectoryView, handlers: TrajectoryHandlers): void;
  clear(): void;
}

/** The two baselines, with the wording the buttons carry. `'start'` first, because it is default. */
const BASELINES: readonly { value: Baseline; label: string; title: string }[] = [
  {
    value: 'start',
    label: 'from step 1',
    title: 'mark the residues that differ from the first point of the trajectory',
  },
  {
    value: 'previous',
    label: 'from the previous step',
    title: 'mark the residues that differ from the point before this one',
  },
];

/** What each unplaceable status says on the facts line, and on the disabled control. */
const UNPLACEABLE: Partial<Record<EntryStatus, { facts: string; title: string }>> = {
  'off-projection': {
    facts: 'not on this projection',
    title: 'this point’s coordinates belong to another projection',
  },
  gone: {
    facts: 'no longer on the map',
    title: 'this decoded point’s record is gone, so there is nothing left to place',
  },
};

export function createTrajectoryPanel(
  root: HTMLElement,
  /** Appended to every widget id, so pane B's controls do not collide with pane A's (P12.1). */
  suffix = '',
): TrajectoryPanel {
  const id = (name: string) => `${name}${suffix}`;

  /**
   * The baseline switch: two buttons, one pressed (`V131`).
   *
   * Buttons rather than a `<select>` because the reader asked for a button, and because
   * `aria-pressed` states the mode to a screen reader without a label having to repeat it. Rendered
   * only when there is something to compare — with a single point both modes show the same thing,
   * which is nothing.
   */
  function baselineSwitch(view: TrajectoryView, handlers: TrajectoryHandlers): HTMLElement {
    const group = el('p', { class: 'baseline', id: id('trajectory-baseline') }, 'differences ');
    for (const option of BASELINES) {
      const button = el(
        'button',
        {
          type: 'button',
          id: id(`trajectory-baseline-${option.value}`),
          title: option.title,
          'aria-pressed': String(view.baseline === option.value),
        },
        option.label,
      );
      button.classList.toggle('selected', view.baseline === option.value);
      button.addEventListener('click', () => handlers.onBaseline(option.value));
      group.append(button);
    }
    return group;
  }

  function entry(
    view: TrajectoryView,
    handlers: TrajectoryHandlers,
    point: TrajectoryPoint,
    at: number,
  ): HTMLElement {
    const step = view.steps[at];
    const status = view.status.get(keyOf(point)) ?? 'placeable';
    const blocked = UNPLACEABLE[status];

    // The ordinal is the button, so the largest, most obvious target in the row is the harmless
    // one. Every destructive or reordering control is a small glyph beside it.
    const ordinal = el(
      'button',
      { type: 'button', class: 'ordinal', title: 'show this point in the detail pane' },
      String(at + 1),
    );
    ordinal.addEventListener('click', () => handlers.onSelect(at));

    const controls = el('span', { class: 'row-controls' });
    for (const [glyph, by, label] of [
      ['↑', -1, 'move earlier in the trajectory'],
      ['↓', 1, 'move later in the trajectory'],
    ] as const) {
      const button = el('button', { type: 'button', class: 'move', title: label }, glyph);
      button.disabled = at + by < 0 || at + by >= view.points.length;
      button.addEventListener('click', () => handlers.onMove(at, by));
      controls.append(button);
    }
    if (status !== 'placed') {
      const place = el(
        'button',
        {
          type: 'button',
          class: 'place',
          title:
            blocked?.title ??
            PLACE_TITLE[point.kind],
        },
        'place',
      );
      place.disabled = blocked !== undefined;
      place.addEventListener('click', () => handlers.onPlace(at));
      controls.append(place);
    }
    // **`×` removes from the walk and leaves the card**, which is why its title says so: the same
    // glyph on a card means "close this card", and the two must not be confused.
    const remove = el(
      'button',
      { type: 'button', class: 'remove', title: 'remove from the trajectory (the card stays)' },
      '×',
    );
    remove.addEventListener('click', () => handlers.onRemove(at));
    controls.append(remove);

    const facts = [originOf(point), step === undefined ? '' : diffLabel(step)].filter(
      (part) => part !== '',
    );
    if (blocked !== undefined) facts.push(blocked.facts);

    const classes = ['step', point.kind];
    if (status === 'placed') classes.push('placed');
    const node = el(
      'li',
      { class: classes.join(' ') },
      el('p', { class: 'head' }, ordinal, el('span', { class: 'facts' }, facts.join(' · ')), controls),
      // **The provenance class, per entry** (P9.5). This is the one surface where all three
      // sequence styles sit one line apart.
      residueSpans(sequenceOf(point), `sequence ${styleOf(point)}`, step?.summary?.positions),
    );
    node.dataset['key'] = keyOf(point);
    return node;
  }

  function show(view: TrajectoryView, handlers: TrajectoryHandlers): void {
    // **Absent, not empty** (`index.html`'s rule: a control that can do nothing is worse than an
    // absent one). With nothing in the walk and the mode elsewhere there is nothing to say; in
    // trajectory mode the same state is worth one line of help, because the reader has just picked
    // up the tool and is looking for where its output will appear.
    if (view.points.length === 0 && !view.active) {
      root.replaceChildren();
      return;
    }

    const section = el(
      'section',
      { class: 'card trajectory', id: id('trajectory-card') },
      el('h2', {}, 'trajectory'),
    );

    if (view.points.length === 0) {
      section.append(
        el(
          'p',
          { class: 'help' },
          'click a point — or a decoded point’s diamond — to start the trajectory',
        ),
      );
      root.replaceChildren(section);
      return;
    }

    section.append(
      el(
        'p',
        { class: 'label' },
        `${view.points.length} point${view.points.length === 1 ? '' : 's'}, start to end`,
      ),
    );
    if (view.points.length > 1) section.append(baselineSwitch(view, handlers));

    const list = el('ol', { class: 'steps' });
    view.points.forEach((point, at) => list.append(entry(view, handlers, point, at)));
    section.append(list);

    const actions = el('p', { class: 'actions' });
    const unplaced = view.points.filter(
      (point) => (view.status.get(keyOf(point)) ?? 'placeable') === 'placeable',
    );
    if (unplaced.length > 0) {
      const all = el(
        'button',
        { type: 'button', id: id('trajectory-place-all'), title: 'pin every point’s card again' },
        `place ${unplaced.length} on the map`,
      );
      all.addEventListener('click', () => handlers.onPlaceAll());
      actions.append(all);
    }
    const clearAll = el(
      'button',
      {
        type: 'button',
        id: id('trajectory-clear'),
        title: 'empty the trajectory — the cards stay on the map',
      },
      'clear the trajectory',
    );
    clearAll.addEventListener('click', () => handlers.onClear());
    actions.append(clearAll);
    section.append(actions);

    root.replaceChildren(section);
  }

  return {
    show,
    clear: () => root.replaceChildren(),
  };
}
